import { ComponentChildren, Fragment } from 'preact';
import { useState, useMemo, useCallback } from 'preact/hooks';
import { memo } from 'preact/compat';
import { cssProps } from '@oracle/oraclejet-cssprops';
import { classNames } from '../utils/UNSAFE_classNames';
import {
  HierarchicalItemContext,
  ListItemContext,
  SelectionDetail,
  ToggleDetail,
  FlattenedDataState
} from '../UNSAFE_Collection';
import { useId } from '../hooks/UNSAFE_useId';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import type { BundleType } from '../resources/nls/bundle';
import { Keys, containsKey, isSameKey } from '../utils/UNSAFE_keys';
import { Button } from '../UNSAFE_Button';
import { HiddenAccessible } from '../UNSAFE_HiddenAccessible';
import { ExpandIcon } from '../PRIVATE_ThemedIcons/ExpandIcon';
import { CollapseIcon } from '../PRIVATE_ThemedIcons/CollapseIcon';
import {
  List,
  ListGroupHeader,
  GROUP_SELECTOR,
  excludeGroup,
  GroupLoadingIndicator
} from '../PRIVATE_List';
import { LiveRegion } from '../UNSAFE_LiveRegion';
import { CustomRendererContext, PublicListProps } from '../UNSAFE_ListView/List.types';
import { useExpandCollapse } from './useExpandCollapse';
import { keyExtractor } from '../utils/PRIVATE_collectionUtils';

// these are the props that are different from ListView
type SpecificTypes<K, D> = {
  /**
   * A function to render an item or a group header.
   */
  children: (context: HierarchicalItemContext<K, D>) => ComponentChildren;
  /**
   * A FlattenedDataState object that provides information including data and metadata to this ExpandableList.
   * If the value is null, then ExpandableList will show loading indicator until a FlattenedDataState is set.
   */
  data: FlattenedDataState<K, D> | null;
  /**
   * A function to handle when user performs a gesture that toggles the expansion state of a group header
   */
  onToggle?: (detail: ToggleDetail<K>) => void;
};

/**
 * Props for the ExpandableList Component, which consist of all props from ListView
 * except some which have different signatures.
 */
type ExpandableListProps<K, D> = Omit<PublicListProps<K, D>, 'children' | 'data'> &
  SpecificTypes<K, D>;

/**
 * An ExpandableList shows a two-level group of items where each group header can be expand or collapse.
 */
export function ExpandableList<K extends string | number, D>({
  children,
  currentKey,
  data,
  onSelectionChange,
  onToggle,
  ...props
}: ExpandableListProps<K, D>) {
  const [statusText, setStatusText] = useState('');
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

  const expandCollapseProps = useExpandCollapse(
    (element: HTMLElement) => {
      if (element.getAttribute('role') === 'treegrid') {
        return currentKey === undefined ? null : currentKey;
      }
      return keyExtractor(element, GROUP_SELECTOR) as K;
    },
    data == null ? (emptyKeys as Keys<K>) : data.expanded,
    onToggle
      ? useCallback(
          (detail: ToggleDetail<K>) => {
            onToggle(detail);
            if (data) {
              const previouslyExpanded = containsKey(data?.expanded, detail.value);
              setStatusText(
                previouslyExpanded
                  ? translations.expandableList_groupCollapse()
                  : translations.expandableList_groupExpand()
              );
              // we need to clear the live region text otherwise screenreader
              // won't read again if the content did not change the next time
              setTimeout(() => {
                setStatusText('');
              }, 1000);
            }
          },
          [onToggle, data]
        )
      : undefined
  );

  const descId = useId();
  const stableDescId = useMemo(() => {
    // this should never change after mounted
    return descId;
  }, []);

  const createHierarchicalContext = <K, D>(
    context: ListItemContext<K, D>,
    options?: { expanded: boolean; onToggle: (detail: ToggleDetail<K>) => void }
  ) => {
    const expanderRenderer =
      options !== undefined
        ? () => (
            <Expander
              itemKey={context.metadata.key}
              expanded={options.expanded}
              onToggle={options.onToggle}
            />
          )
        : undefined;

    return {
      ...context,
      parentKey: context.metadata.parentKey,
      leaf: context.metadata.isLeaf ?? true,
      depth: context.metadata.treeDepth ?? 1,
      expander: expanderRenderer
    };
  };

  const handleSelectionChange = useCallback(
    (detail: SelectionDetail<K>) => {
      if (onSelectionChange && data) {
        const keys = excludeGroup(data, detail.value);
        if (isSameKey(keys, detail.value)) {
          onSelectionChange(detail);
        } else if (keys.keys && keys.keys.size > 0) {
          onSelectionChange({ ...detail, value: keys });
        }
      }
    },
    [onSelectionChange, data]
  );

  const customItemRenderer = (context: CustomRendererContext<K, D>) => {
    const itemKey = context.listItemContext.metadata.key;
    const options =
      data && onToggle ? { expanded: containsKey(data.expanded, itemKey), onToggle } : undefined;
    const itemContext = createHierarchicalContext(context.listItemContext, options);
    if (itemContext.leaf) {
      return context.defaultListItem(itemContext, { itemDepth: itemContext.depth + 1 });
    } else {
      return (
        <ListGroupHeader
          key={itemContext.metadata.key}
          ariaDescribedBy={stableDescId}
          itemKey={itemContext.metadata.key}
          itemIndex={itemContext.index}
          itemDepth={itemContext.depth}
          isFocused={context.isFocused}
          isFocusRingVisible={context.isFocusRingVisible}
          isActive={context.isActive}
          isGridlineVisible={false}
          isExpandable={true}
          expandedKeys={data == null ? (emptyKeys as Keys<K>) : data.expanded}
          onToggle={onToggle}>
          {children(itemContext)}
        </ListGroupHeader>
      );
    }
  };

  return (
    <Fragment>
      <List
        isVirtualized={false}
        currentKey={currentKey}
        data={data}
        role="treegrid"
        loadingIndicator={<GroupLoadingIndicator />}
        onSelectionChange={handleSelectionChange}
        customItemRenderer={customItemRenderer}
        eventHandlers={expandCollapseProps}
        {...props}>
        {(context: ListItemContext<K, D>) => {
          return children(context as HierarchicalItemContext<K, D>);
        }}
      </List>
      {onToggle && (
        <Fragment>
          <LiveRegion>{statusText}</LiveRegion>
          <span id={stableDescId}>
            <HiddenAccessible>
              {translations.expandableList_expandCollapseInstructionText()}
            </HiddenAccessible>
          </span>
        </Fragment>
      )}
    </Fragment>
  );
}

const emptyKeys = { all: false, keys: new Set() };

const Expander = memo(
  <K,>(props: { itemKey: K; expanded: boolean; onToggle: (detail: ToggleDetail<K>) => void }) => {
    const styles = {
      base: cssProps`${{
        margin: '0 calc(1.25 * var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-1x))'
      }}`
    };

    const classes = classNames([styles.base, 'oj-listview-expander']);

    const actionHandler = () => {
      props.onToggle({ value: props.itemKey });
    };

    return (
      <span class={classes} role="presentation">
        <Button
          onAction={actionHandler}
          variant="ghost"
          startIcon={props.expanded ? <ExpandIcon size="6x" /> : <CollapseIcon size="6x" />}
        />
      </span>
    );
  },
  (prev: any, next: any) => {
    return (
      prev.itemKey === next.itemKey &&
      prev.expanded === next.expanded &&
      prev.onToggle === next.onToggle
    );
  }
);
